import qs from 'qs';

import type { operations } from '@luxuryescapes/contract-support/dist/server';

import { Chat } from '~/components/SupportAssistant/types/Chat';

import type { TicketRiskBand, TicketRiskReport, TicketRiskScoreHistory } from '~/types/ticketRisk';

import { json_headers, request } from './common';

const basePath = () => `${window.configs.API_HOST}/api/support/v1`;

const arrayQueryParamKeys = ['chatTopics', 'regionCodes'];

const stringifyQueryParams = (params: any) => {
  const normalizedParams = {
    ...params,
    ...arrayQueryParamKeys.reduce((arrayParams, key) => {
      if (!Array.isArray(params[key])) {
        return arrayParams;
      }

      return {
        ...arrayParams,
        [key]: params[key].length === 1 ? [params[key][0], params[key][0]] : params[key],
      };
    }, {}),
  };

  return qs.stringify(normalizedParams, { arrayFormat: 'repeat', skipNulls: true });
};

type queryFilter = {
  from: string;
  to: string;
  customersOnly: boolean;
  agentChatsOnly: boolean;
  hasMessagesOnly: boolean;
  assistantVersion?: 'V1' | 'V2';
};

export const searchChats = (params: any) => {
  const queryParams = stringifyQueryParams(params);
  const uri = `${basePath()}/chats/search?${queryParams}`;

  return request(uri, { method: 'GET' });
};

export const getChatById = (id: string) => {
  const uri = `${basePath()}/chats/${id}?includeMessages=true`;

  return request(uri, { method: 'GET' });
};

export const getArticles = (params: any) => {
  const queryParams = qs.stringify(params);
  const uri = `${basePath()}/articles?${queryParams}`;

  return request(uri, { method: 'GET' });
};

export const getFeedback = (params: any) => {
  const queryParams = qs.stringify(params);
  const uri = `${basePath()}/feedbacks?${queryParams}`;

  return request(uri, { method: 'GET' });
};

export const getDailyStats = (filter: queryFilter) => {
  const query = qs.stringify(filter);

  const uri = `${basePath()}/statistics/daily-stats?${query}`;

  return request(uri, { method: 'GET' });
};

export const getChatStats = (params: any) => {
  const query = qs.stringify(params);

  const uri = `${basePath()}/statistics/chats-time-stats?${query}`;

  return request(uri, { method: 'GET' });
};

export const getFeedbackIssuesStats = (filter: queryFilter) => {
  const query = qs.stringify(filter);

  const uri = `${basePath()}/statistics/feedback-reasons-count?${query}`;

  return request(uri, { method: 'GET' });
};

export const updateChat = (chat: Partial<Chat>) => {
  const uri = `${basePath()}/chats/${chat.id}`;

  return request(uri, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      quality: chat.quality,
      followUp: chat.followUp,
      deflection: chat.deflection,
      topics: chat.topics,
    }),
  });
};

export const getChatsTopicsFrequency = (filter: queryFilter) => {
  const query = qs.stringify(filter);

  const uri = `${basePath()}/statistics/chats-topics-frequency?${query}`;

  return request(uri, { method: 'GET' });
};

export const getChatTopics = () => {
  const uri = `${basePath()}/chats/topics`;

  return request(uri, { method: 'GET' });
};

export const exportChatsStream = (params: any) => {
  const queryParams = stringifyQueryParams(params);
  const uri = `${basePath()}/chats/export-stream?${queryParams}`;

  return request(uri, {
    method: 'GET',
    returnResponse: true, // Return the raw response for streaming
  });
};

export const getChristmasMessage = () => {
  const uri = `${basePath()}/christmas-message`;

  return request(uri, { method: 'GET' });
};

export const getSettings = () => {
  const uri = `${basePath()}/settings`;

  return request(uri, { method: 'GET' });
};

export const setSettings = (body) => {
  const uri = `${basePath()}/settings`;

  return request(uri, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify(body),
  });
};

export const getSettingsHistory = () => {
  const uri = `${basePath()}/settings/history`;

  return request(uri, { method: 'GET' });
};

export const getCancellationModalSettings = () => {
  const uri = `${basePath()}/cancellation-modal-settings`;
  return request(uri, {
    method: 'GET',
  });
};

export const setCancellationModalSettings = (data) => {
  const uri = `${basePath()}/cancellation-modal-settings`;
  return request(uri, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify(data),
  });
};

export interface SiteBannerCampaign {
  id: string;
  campaignTitle: string;
  bannerTitle: string;
  bannerOverview: string;
  status: 'DRAFT' | 'ACTIVE' | 'ARCHIVED';
  contentType: 'banner';
  modalTitle: string;
  modalDescription: string;
  startDate: Date;
  endDate: Date;
  targets: Array<any>;
  settings: {
    bannerIcon: 'check' | 'info' | 'warning' | 'error';
    bannerColor: 'info' | 'success' | 'warning' | 'error';
  };
}

type APIGetBannersCampaignListResponse =
  operations['get-targeted-banners']['responses']['200']['content']['application/json'];
type APIGetBannerCampaignResponse =
  operations['get-targeted-banner']['responses']['200']['content']['application/json'];
type APICreateBannerPayload = operations['create-targeted-banner']['requestBody']['content']['application/json'];
type APIUpdateBannerPayload = operations['update-targeted-banner']['requestBody']['content']['application/json'];

function mapBannerCampaign(campaign: APIGetBannerCampaignResponse['result']): SiteBannerCampaign {
  return {
    id: campaign.id,
    campaignTitle: campaign.campaignTitle,
    bannerTitle: campaign.bannerTitle,
    bannerOverview: campaign.bannerOverview,
    status: campaign.status,
    contentType: 'banner',
    modalTitle: campaign.modalTitle,
    modalDescription: campaign.modalDescription,
    startDate: new Date(campaign.startDate),
    endDate: new Date(campaign.endDate),
    targets: campaign.targets as unknown as Array<any>,
    settings: campaign.settings as {
      bannerIcon: 'check' | 'info' | 'warning' | 'error';
      bannerColor: 'info' | 'success' | 'warning' | 'error';
    },
  };
}

export async function getBannerCampaignsList(
  page: number,
  rowsPerPage: number,
  brand: string,
): Promise<{ banners: Array<SiteBannerCampaign>; count: number }> {
  const params = new URLSearchParams({ page: `${page}`, limit: `${rowsPerPage}`, brand });
  const uri = `${basePath()}/targeted-banners?${params.toString()}`;
  const response: APIGetBannersCampaignListResponse = await request(uri, { method: 'GET' });
  return {
    banners: response.result.map(mapBannerCampaign),
    count: response.count,
  };
}

export async function getBannerCampaign(id: string): Promise<SiteBannerCampaign> {
  const uri = `${basePath()}/targeted-banners/${id}`;
  const banner = await request(uri, { method: 'GET' });
  return mapBannerCampaign(banner.result);
}

export async function createBannerCampaign(data: APICreateBannerPayload): Promise<void> {
  const uri = `${basePath()}/targeted-banners`;
  return request(uri, {
    method: 'POST',
    headers: json_headers,
    body: JSON.stringify(data),
  });
}

export async function updateBannerCampaign(id: string, data: APIUpdateBannerPayload): Promise<void> {
  const uri = `${basePath()}/targeted-banners/${id}`;
  return request(uri, {
    method: 'PATCH',
    headers: json_headers,
    body: JSON.stringify(data),
  });
}

/**
 * Latest ticket risk scoring run from svc-support: every scored ticket with its facts,
 * score components and band. Throws an ApiError with `status: 404` when no scoring run
 * has been persisted yet.
 */
export async function getTicketRiskReport(
  params: { band?: TicketRiskBand } = {},
  options: { signal?: AbortSignal } = {},
): Promise<TicketRiskReport> {
  const query = qs.stringify(params, { skipNulls: true });
  const uri = `${basePath()}/ticket-risk/report${query ? `?${query}` : ''}`;
  const response: { result: TicketRiskReport } = await request(uri, {
    method: 'GET',
    signal: options.signal,
  });

  return response.result;
}

/**
 * Every persisted scoring run for one ticket as a trend line, oldest point first. Throws an
 * ApiError with `status: 404` when the ticket has never been scored.
 */
export async function getTicketRiskHistory(
  ticketId: string,
  params: { limit?: number } = {},
  options: { signal?: AbortSignal } = {},
): Promise<TicketRiskScoreHistory> {
  const query = qs.stringify(params, { skipNulls: true });
  const uri = `${basePath()}/ticket-risk/${encodeURIComponent(ticketId)}/history${query ? `?${query}` : ''}`;
  const response: { result: TicketRiskScoreHistory } = await request(uri, {
    method: 'GET',
    signal: options.signal,
  });

  return response.result;
}
